import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { shared } from '../styles/shared.js';
import { LocaleController } from '../i18n/locale-controller.js';
import { t, type Locale } from '../i18n/t.js';
import { getProgression } from '../progression/context.js';
import { SKILLS, type Skill } from '../storage/types.js';
import { MAX_LEVEL } from '../progression/levels.js';

/**
 * Parent settings panel. Always rendered behind the parent gate (the gate
 * lives in `<app-root>`). Three controls:
 *  - Locale switch
 *  - Reset progress (with in-panel confirmation)
 *  - Per-skill level inspector + manual up/down override
 */
@customElement('parent-settings')
export class ParentSettings extends LitElement {
  static override styles = [
    shared,
    css`
      .panel {
        max-width: 720px;
        width: 90vw;
        max-height: 80vh;
        overflow: auto;
        display: flex;
        flex-direction: column;
        gap: var(--space-5);
      }

      /* On phone landscape the panel was eating ~90% of the viewport, leaving
         almost no backdrop to tap. Shrink it so tap-outside-to-close has room. */
      @media (max-height: 500px) {
        .panel {
          width: 75vw;
          max-height: 75vh;
          gap: var(--space-3);
          padding: var(--space-4);
        }
      }

      h2 {
        margin: 0;
      }

      .row {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        flex-wrap: wrap;
      }

      .row label {
        flex: 1 1 200px;
      }

      .skill-row {
        display: grid;
        grid-template-columns: 1fr auto auto auto;
        gap: var(--space-3);
        align-items: center;
      }

      .skill-row .level {
        font-family: var(--font-display);
        font-weight: 700;
        min-width: 100px;
        text-align: center;
      }

      .button-icon {
        min-height: 48px;
        min-width: 48px;
        padding: var(--space-2);
        border-radius: var(--radius-md);
        font-size: var(--type-l);
        background: var(--color-accent-cyan);
      }

      .danger {
        background: var(--color-accent-pink);
        color: white;
      }

      .reset-confirm {
        background: rgba(255, 93, 143, 0.1);
        padding: var(--space-3);
        border-radius: var(--radius-md);
        display: flex;
        gap: var(--space-3);
      }
    `,
  ];

  @property({ type: Boolean, reflect: true }) open = false;
  @state() private confirmingReset = false;
  @state() private updateTick = 0;

  private locale = new LocaleController(this);
  private unsubscribe?: () => void;

  override connectedCallback(): void {
    super.connectedCallback();
    try {
      this.unsubscribe = getProgression().subscribe(() => {
        this.updateTick++;
      });
    } catch {
      // Progression not initialised in some test contexts; ignore.
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = undefined;
  }

  private async onLocaleChange(loc: Locale): Promise<void> {
    this.locale.setLocale(loc);
    try {
      await getProgression().setLocale(loc);
    } catch {
      // Progression not available; locale change still applied in-memory.
    }
  }

  private async onAdjustLevel(skill: Skill, delta: number): Promise<void> {
    const progression = getProgression();
    const newLevel = progression.getLevel(skill) + delta;
    await progression.setLevel(skill, newLevel);
  }

  private async onConfirmReset(): Promise<void> {
    await getProgression().reset();
    this.confirmingReset = false;
  }

  private onClose(): void {
    this.open = false;
    this.confirmingReset = false;
    this.dispatchEvent(new CustomEvent('settings-close', { bubbles: true, composed: true }));
  }

  private onModalClick(e: MouseEvent): void {
    // Close on any tap that lands on the modal wrapper itself (i.e. outside the
    // panel) — not just on the thin backdrop layer. Catches mobile-landscape
    // taps that hit the flex padding around the panel.
    if (e.target === e.currentTarget) this.onClose();
  }

  private renderSkillRow(skill: Skill): unknown {
    void this.updateTick; // touch the reactive state so this row re-renders on changes
    let level = 1;
    try {
      level = getProgression().getLevel(skill);
    } catch {
      /* Progression unavailable in some test fixtures. */
    }
    const labelKey = `settings.skill.${skill}` as const;
    const loc = this.locale.locale;
    return html`<div class="skill-row" data-testid=${`skill-row-${skill}`}>
      <span class="text-body">${t(loc, labelKey)}</span>
      <span class="level" data-testid=${`skill-level-${skill}`}
        >${t(loc, 'settings.skill.level', { level })}</span
      >
      <button
        class="button-icon"
        aria-label=${t(loc, 'settings.skill.down')}
        ?disabled=${level <= 1}
        data-testid=${`skill-down-${skill}`}
        @click=${() => this.onAdjustLevel(skill, -1)}
      >
        −
      </button>
      <button
        class="button-icon"
        aria-label=${t(loc, 'settings.skill.up')}
        ?disabled=${level >= MAX_LEVEL[skill]}
        data-testid=${`skill-up-${skill}`}
        @click=${() => this.onAdjustLevel(skill, +1)}
      >
        +
      </button>
    </div>`;
  }

  override render() {
    if (!this.open) return html``;
    const loc = this.locale.locale;
    return html`<div class="modal" data-testid="parent-settings" @click=${this.onModalClick}>
      <div class="modal__backdrop" @click=${this.onClose}></div>
      <div class="modal__panel panel">
        <div class="row">
          <h2 class="text-display">${t(loc, 'settings.title')}</h2>
          <button
            class="button-quiet"
            aria-label=${t(loc, 'settings.close')}
            @click=${this.onClose}
          >
            ✕
          </button>
        </div>

        <div class="row">
          <label class="text-body">${t(loc, 'settings.locale.label')}</label>
          <button
            class=${'button-primary' + (loc === 'nl' ? '' : ' button-quiet')}
            data-testid="locale-nl"
            @click=${() => this.onLocaleChange('nl')}
          >
            ${t(loc, 'settings.locale.nl')}
          </button>
          <button
            class=${'button-primary' + (loc === 'en' ? '' : ' button-quiet')}
            data-testid="locale-en"
            @click=${() => this.onLocaleChange('en')}
          >
            ${t(loc, 'settings.locale.en')}
          </button>
        </div>

        <h3 class="text-display" style="font-size: var(--type-m);">
          ${t(loc, 'settings.skills.title')}
        </h3>
        ${SKILLS.map((s) => this.renderSkillRow(s))}
        ${this.confirmingReset
          ? html`<div class="reset-confirm" data-testid="reset-confirm">
              <p class="text-body" style="flex: 1;">${t(loc, 'settings.reset.confirm.title')}</p>
              <button
                class="button-primary danger"
                data-testid="reset-yes"
                @click=${this.onConfirmReset}
              >
                ${t(loc, 'settings.reset.confirm.yes')}
              </button>
              <button class="button-primary" @click=${() => (this.confirmingReset = false)}>
                ${t(loc, 'settings.reset.confirm.no')}
              </button>
            </div>`
          : html`<button
              class="button-primary danger"
              data-testid="reset-progress"
              @click=${() => (this.confirmingReset = true)}
            >
              ${t(loc, 'settings.reset')}
            </button>`}
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'parent-settings': ParentSettings;
  }
}
